// F4 Report 13 (F5): Per-project stage breakdown.
// Top filter: project picker (typeahead) -> auto-loads net amount and stage breakdown.

import React, { useEffect, useRef, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  fetchReportProjectBreakdown,
  searchProjects,
} from "../../api/service";
import ReportShell, { display, fmtINR, downloadReportCsv } from "./ReportShell";

// Lightweight typeahead picker (mirrors PaymentsDashboard's pattern).
function ProjectPicker({ onChange, value }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 1) {
      setOptions([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await searchProjects(query);
        setOptions(res?.results || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: "relative", maxWidth: 480 }}>
      <Form.Control
        type="text"
        value={value || query}
        placeholder="Search project by name or id..."
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && (loading || options.length > 0) && (
        <div
          className="border bg-white shadow-sm"
          style={{
            position: "absolute", top: "100%", left: 0, right: 0,
            zIndex: 100, maxHeight: 240, overflowY: "auto",
          }}
        >
          {loading && <div className="px-3 py-2 text-muted">Searching...</div>}
          {!loading && options.map((opt) => (
            <div
              key={opt.id}
              className="px-3 py-2"
              role="button"
              style={{ cursor: "pointer" }}
              onMouseDown={() => { onChange(opt); setQuery(""); setOpen(false); }}
            >
              <strong>Proj 00{opt.id}</strong>
              {opt.display ? <span className="text-muted ms-2">— {opt.display.replace(/^Proj\s+\d+_?/, '').replace(/_/g, ' ').trim()}</span> : null}
            </div>
          ))}
          {!loading && options.length === 0 && query && (
            <div className="px-3 py-2 text-muted">No matches</div>
          )}
        </div>
      )}
    </div>
  );
}

const ProjectBreakdownReport = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selected) { setData(null); return; }
    let alive = true;
    setLoading(true); setError(null);
    fetchReportProjectBreakdown(selected.id)
      .then((res) => { if (alive) setData(res); })
      .catch((e) => alive && setError(e?.message || "Failed to load"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [selected]);

  const renderDays = (days) => {
    if (typeof days === "number") return days;
    return <span className="text-muted">{days}</span>;
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <button
            className="btn btn-link p-0 mb-1"
            onClick={() => navigate("/reports")}
            style={{ textDecoration: "none" }}
          >
            ← Back to Reports
          </button>
          <h4 className="mb-0">13. Project Stage Breakdown</h4>
          <small className="text-muted">F5 — pick a project to see days spent in each stage</small>
        </div>
        {data && (
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => downloadReportCsv("project_breakdown", `?project_id=${data.project_id}`)}
          >
            Export CSV
          </button>
        )}
      </div>

      <div className="mb-3">
        <Form.Label>Project</Form.Label>
        <ProjectPicker
          value={selected ? `Proj 00${selected.id}` : null}
          onChange={(opt) => setSelected(opt)}
        />
        {selected && (
          <div className="mt-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => { setSelected(null); setData(null); }}
            >
              Clear selection
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {!loading && data && (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div><strong>Project:</strong> {data.project_label}</div>
                  <div><strong>Status:</strong> {display(data.project_status)}</div>
                </div>
                <div className="col-md-6">
                  <div><strong>Net Value (INR):</strong> {fmtINR(data.net_value)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th className="text-center">Stage</th>
                  <th className="text-center">Days</th>
                </tr>
              </thead>
              <tbody>
                {data.stages.map((s, idx) => (
                  <tr key={idx}>
                    <td className="text-center">{s.stage}</td>
                    <td className="text-center">{renderDays(s.days)}</td>
                  </tr>
                ))}
                <tr className="table-info fw-bold">
                  <td className="text-center">Grand Total {data.partial && <small className="fw-normal text-muted">(partial — some stages not completed)</small>}</td>
                  <td className="text-center">{data.grand_total}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !selected && !data && (
        <div className="text-muted">Select a project to view its stage breakdown.</div>
      )}
    </div>
  );
};

export default ProjectBreakdownReport;
